import type {
  DurationData,
  ExceptionData,
  AccumulateData,
  DataTypeMap,
  IMonitor,
  Handler,
  UnsubFunc,
} from './types';
import { DataType } from './enums';

export class Monitor implements IMonitor {
  private buffers: {
    [DataType.Accumulate]?: AccumulateData[];
    [DataType.Exception]?: ExceptionData[];
    [DataType.Duration]?: DurationData[];
  };

  private subscribers: {
    [DataType.Accumulate]: Handler<AccumulateData>[];
    [DataType.Exception]: Handler<ExceptionData>[];
    [DataType.Duration]: Handler<DurationData>[];
  };

  constructor() {
    this.buffers = {
      [DataType.Duration]: [],
      [DataType.Exception]: [],
      [DataType.Accumulate]: [],
    };
    this.subscribers = {
      [DataType.Duration]: [],
      [DataType.Exception]: [],
      [DataType.Accumulate]: [],
    };
  }

  // Monitor should buffer the data until a subscriber is added,
  // as the monitoring SDKs are usually lazy initialised.

  // handler will receive the buffered data when being added.

  subscribeAccumulate(handler: Handler<AccumulateData>): UnsubFunc {
    return this.subscribe(DataType.Accumulate, handler);
  }

  subscribeException(handler: Handler<ExceptionData>): UnsubFunc {
    return this.subscribe(DataType.Exception, handler);
  }

  subscribeDuration(handler: Handler<DurationData>): UnsubFunc {
    return this.subscribe(DataType.Duration, handler);
  }

  capture(exception: ExceptionData) {
    this.sendData(DataType.Exception, exception);
  }

  sendDuration(duration: DurationData) {
    this.sendData(DataType.Duration, duration);
  }

  sendAccumulate(accumulate: AccumulateData) {
    this.sendData(DataType.Accumulate, accumulate);
  }

  private subscribe<T extends DataType, D = DataTypeMap[T]>(
    t: T,
    handler: Handler<D>
  ): UnsubFunc {
    const buf = this.buffers[t] as D[];
    buf?.forEach(e => {
      handler(e);
    });
    this.buffers[t] = void 0;

    (this.subscribers[t] as Handler<D>[]).push(handler);

    return () => {
      const list = this.subscribers[t] as Handler<D>[];
      const newList = list.filter(f => f !== handler);
      this.subscribers[t] = newList as any;

      if (newList.length) {
        this.buffers[t] = [];
      }
    };
  }

  private sendData<T extends DataType, D = DataTypeMap[T]>(t: T, data: D) {
    (this.buffers[t] as D[])?.push(data);
    const cbs = this.subscribers[t] as Handler<D>[];
    for (let i = 0; i < cbs.length; i++) {
      const cb = cbs[i];
      cb(data);
    }
  }
}
